import { VALIDITY_OPTIONS, type CreatorDraftPayload } from './draftSchema';

type Props = {
  payload: CreatorDraftPayload;
  onChange: (partial: Partial<CreatorDraftPayload>) => void;
};

const parseRupees = (raw: string): number | undefined => {
  const cleaned = raw.replace(/[^\d.]/g, '');
  if (!cleaned) return undefined;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 100);
};

const formatRupees = (paise: number | undefined): string => {
  if (paise == null) return '';
  return String(Math.round(paise / 100));
};

const parseMonths = (raw: string): number | undefined => {
  const cleaned = raw.replace(/[^\d]/g, '');
  if (!cleaned) return undefined;
  const value = Number(cleaned);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

const PRESET_DAYS = new Set(VALIDITY_OPTIONS.map((o) => o.days));

const StepPricing = ({ payload, onChange }: Props) => {
  const hasValidity = payload.validityDays != null;
  // A non-preset, positive day count came from the custom months input.
  const isCustomValidity =
    payload.validityDays != null && payload.validityDays > 0 && !PRESET_DAYS.has(payload.validityDays);
  const customMonths = isCustomValidity ? String(Math.round((payload.validityDays as number) / 30)) : '';

  return (
    <div className="creator-step" data-testid="step-pricing">
      <header className="creator-step__header">
        <span className="creator-step__index">2</span>
        <div>
          <h2 className="creator-step__title">Pricing</h2>
          <p className="creator-step__subtitle">
            First choose how long it stays active, then set the price.
          </p>
        </div>
      </header>

      <fieldset className="creator-field">
        <legend className="creator-field__label">VALIDITY</legend>
        <div className="creator-validity-grid">
          {VALIDITY_OPTIONS.map((option) => {
            const isSelected = payload.validityDays === option.days;
            return (
              <button
                key={option.days}
                type="button"
                className={`creator-chip${isSelected ? ' creator-chip--active' : ''}`}
                onClick={() => onChange({ validityDays: option.days })}
                data-testid={`creator-pricing-validity-${option.days}`}
                aria-pressed={isSelected}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div className={`creator-validity-custom${isCustomValidity ? ' creator-validity-custom--active' : ''}`}>
          <span className="creator-validity-custom__or">or</span>
          <input
            type="text"
            inputMode="numeric"
            className="creator-field__input creator-validity-custom__input"
            placeholder="9"
            value={customMonths}
            onChange={(event) => {
              const months = parseMonths(event.target.value);
              onChange({ validityDays: months == null ? undefined : months * 30 });
            }}
            data-testid="creator-pricing-validity-custom"
            aria-label="Custom validity in months"
          />
          <span className="creator-validity-custom__unit">months</span>
        </div>
        <span className="creator-field__hint">Pick a preset or enter any number of months. Lifetime never expires.</span>
      </fieldset>

      {hasValidity ? (
        <label className="creator-field" data-testid="creator-pricing-price-field">
          <span className="creator-field__label">PRICE</span>
          <div className="creator-field__currency">
            <span className="creator-field__currency-symbol">₹</span>
            <input
              type="text"
              inputMode="numeric"
              className="creator-field__input"
              placeholder="2,999"
              value={formatRupees(payload.pricePaise)}
              onChange={(event) => onChange({ pricePaise: parseRupees(event.target.value) ?? 0 })}
              data-testid="creator-pricing-price"
              autoFocus
            />
          </div>
          <span className="creator-field__hint">
            What the customer pays to join. Enter <strong>0</strong> for a free membership.
          </span>
        </label>
      ) : (
        <p className="creator-step__hint-muted" data-testid="creator-pricing-price-locked">
          Pick a validity above, then set the price.
        </p>
      )}
    </div>
  );
};

export default StepPricing;
